import { bookSeed, hashString } from '../color.js';
import { PROPS } from './sprites.js';
import { caseRanges } from './ranges.js';

// The level: where every bookcase, book and prop stands, in world pixels.
//
// It is a plain function of the shelves rather than something the component
// works out while it renders. The camera needs the whole level's width before
// it can draw a frame of it, and this is the part of the theme most worth
// reading and checking without a browser in front of you.
//
// World pixels are the console's, not the screen's. Everything here is a small
// integer; the stage's transform is the only thing that knows about the actual
// display, which is what keeps the art on the pixel grid at any size.

// A bookcase is one piece of furniture and every one of them is identical: the
// same five shelves, the same nine books to a shelf, the same height and the
// same width, wherever it stands and whatever is on it.
//
// It used to size itself to its contents - more books meant a wider, taller
// cabinet - which made a shelf's size readable at a glance and made the room
// look like a warehouse of mismatched units. One size means a shelf's size is
// read off the status bar and the badges instead, and the room looks like a
// room.
export const SHELVES = 5;
export const PER_SHELF = 9;
export const PER_CASE = SHELVES * PER_SHELF; // 45

export const FLOOR_H = 22; // floor plane, below the bookcases
// The reader walks along the front of the floor and the furniture stands at
// the back of it. There is no perspective in a side view, so this gap is the
// only thing that says the reader is in the room rather than on the shelf -
// without it a sprite drawn at the same height as the bookcase's base looks
// like it is standing inside the bookcase. Here rather than in Level.jsx
// because the loading screen and a season's decoration stand him there too.
export const STANDS_AT = 5;
export const FRAME = 3; // thickness of a bookcase's outer frame
export const BOARD = 2; // thickness of one shelf board inside it
export const SHELF_H = 25; // clear height under each board

// Wide enough for nine of the widest books with a pixel between each, since
// the width cannot flex to the books that turn up on a shelf.
const BOOK_GAP = 1;
const WIDEST_BOOK = 7;
export const CASE_INNER_W = PER_SHELF * WIDEST_BOOK + (PER_SHELF - 1) * BOOK_GAP;
export const CASE_W = CASE_INNER_W + FRAME * 2;
export const CASE_H = FRAME + SHELVES * SHELF_H + (SHELVES - 1) * BOARD + FRAME;

const CASE_GAP = 18; // between two bookcases holding one shelf
const ZONE_GAP = 46; // between one shelf and the next
const EDGE = 52; // so the first and last bookcase are not against the frame

// Tallest a book may be drawn, leaving daylight under the board above it: a
// shelf packed to the board reads as a solid block rather than as books.
const MAX_BOOK_H = SHELF_H - 3;
const MIN_BOOK_H = 16;

// Three thicknesses rather than the seven this had, which is not a loss of
// information so much as a change of what the width is for. It was a reading
// of the page count; now that a shelf is nine books wide whatever they are, it
// is what decides how much of the shelf is left empty - and seven widths meant
// a shelf of thin books sat in the left half of its bookcase looking unfinished.
function bookWidth(book) {
  const pages = book.pages || 250;
  if (pages < 250) return 5;
  if (pages < 450) return 6;
  return WIDEST_BOOK;
}

// Height hashed off the title, so a row has the ragged top edge a real shelf
// does and keeps it across reloads - and keeps the same one the classic shelf
// gave the book, since both hash the same seed.
function bookHeight(book) {
  return MIN_BOOK_H + (hashString(bookSeed(book)) % (MAX_BOOK_H - MIN_BOOK_H + 1));
}

// How a shelf's books divide between bookcases.
//
// Evenly, rather than filling each cabinet before starting the next. Forty-six
// books fill one bookcase and leave one book alone in a second full-height
// cabinet, which reads as a fault; twenty-three and twenty-three reads as a
// pair of bookcases.
//
// `expected` is how many books the shelf will have once every page of it has
// arrived - see the preload in App.jsx - and it is what the bookcases are
// counted and sized for while the shelf is still filling. So the room is its
// finished size from the first page: every bookcase is standing, and the books
// that have arrived fill them in order, left to right. Without it the room
// grew a bookcase at a time and the camera, the zones and the reader's walk
// all moved with every page.
function splitIntoCases(books, expected = 0) {
  const total = Math.max(books.length, expected);
  const caseCount = Math.max(1, Math.ceil(total / PER_CASE));
  const cases = [];
  let at = 0;
  for (let i = 0; i < caseCount; i++) {
    const size = Math.floor(total / caseCount) + (i < total % caseCount ? 1 : 0);
    cases.push(books.slice(at, at + size));
    at += size;
  }
  return cases;
}

// Books onto the shelves of one bookcase: nine to a shelf, filling from the
// top down, with whatever shelves are left over standing empty. That is what a
// bookcase someone is still filling looks like.
function shelveInto(books) {
  const rows = [];
  for (let i = 0; i < books.length; i += PER_SHELF) rows.push(books.slice(i, i + PER_SHELF));

  return rows.map((row, fromTop) => {
    // Rows are positioned up from the bookcase's base, and reading order runs
    // down from its top shelf.
    const fromBottom = SHELVES - 1 - fromTop;
    let left = 0;
    return {
      bottom: FRAME + fromBottom * (SHELF_H + BOARD),
      books: row.map((book) => {
        const w = bookWidth(book);
        const slot = { book, w, h: bookHeight(book), left };
        left += w + BOOK_GAP;
        return slot;
      }),
    };
  });
}

// Props are handed out in order rather than at random, so a display that
// reloads comes back to the same room. Whatever fits the gap it is given.
function propFor(gap, seed) {
  const fits = PROPS.filter((prop) => prop.sprite.width + 4 <= gap);
  if (!fits.length) return null;
  return fits[seed % fits.length];
}

// `expected`, when it is given, is the number of books each shelf will have,
// keyed as `shelves` is - see splitIntoCases.
export function buildLevel(shelves, settings = {}, expected = null) {
  const cases = [];
  const props = [];
  const zones = [];
  const placed = []; // every book, with where in the level it ended up
  // The stretches of floor with no bookcase on them - the margin at each end
  // and every gap between two cases. Props stand in them, and so does anything
  // a season hangs on the wall, which is why they are kept rather than being a
  // side effect of placing props.
  const gaps = [{ from: 0, to: EDGE }];

  let x = EDGE;
  let gapSeed = 0;

  shelves.forEach((shelf, shelfIndex) => {
    if (shelfIndex > 0) {
      const prop = propFor(ZONE_GAP, gapSeed++);
      if (prop) props.push({ ...prop, x: Math.round(x + (ZONE_GAP - prop.sprite.width) / 2) });
      gaps.push({ from: x, to: x + ZONE_GAP });
      x += ZONE_GAP;
    }

    const due = expected?.[shelf.shelfKey] ?? 0;
    const split = splitIntoCases(shelf.books, due);
    // What each bookcase is labelled with under the shelf's own sign. Worked
    // out across the whole shelf at once, because the shortest abbreviation
    // that tells two bookcases apart depends on both of them - and so not at
    // all while the shelf is still arriving, when a case may be empty and the
    // span of the rest is not the span it will have. The space is kept either
    // way, so nothing moves when the badges appear.
    const filling = shelf.books.length < due;
    const ranges = filling ? split.map(() => null) : caseRanges(split, settings.sortOrder);
    const from = x;

    split.forEach((caseBooks, caseIndex) => {
      if (caseIndex > 0) {
        const prop = propFor(CASE_GAP, gapSeed++);
        if (prop) props.push({ ...prop, x: Math.round(x + (CASE_GAP - prop.sprite.width) / 2) });
        gaps.push({ from: x, to: x + CASE_GAP });
        x += CASE_GAP;
      }

      const entry = {
        shelfKey: shelf.shelfKey,
        x,
        caseIndex,
        caseCount: split.length,
        range: ranges[caseIndex],
        rows: shelveInto(caseBooks),
      };

      for (const row of entry.rows) {
        for (const slot of row.books) {
          placed.push({
            book: slot.book,
            shelfKey: shelf.shelfKey,
            // Where the book actually is, which the reader needs in order to
            // walk to it, work out whether he can reach it, and leave a gap
            // on the shelf where it was.
            worldX: x + FRAME + slot.left + slot.w / 2,
            left: x + FRAME + slot.left,
            shelfY: row.bottom, // above the bookcase's base, which is the room's floor
            w: slot.w,
            h: slot.h,
          });
        }
      }

      cases.push(entry);
      x += CASE_W;
    });

    zones.push({ shelfKey: shelf.shelfKey, from, to: x, count: shelf.books.length });
  });

  // Wall furniture is not here on purpose: how many rows of pictures a wall
  // has depends on how tall the screen made the room, which this cannot know.
  // See wallBands in Level.jsx.
  gaps.push({ from: x, to: x + EDGE });
  return { width: x + EDGE, cases, props, zones, placed, gaps };
}
